import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { colorShade } from '../utils/style';

export enum ButtonColor { PRIMARY, SECONDARY, TRANSPARENT }
export enum ButtonSize { SMALL, MEDIUM, LARGE }
export enum ButtonWidth { FULL, CONTENT, AUTO }

interface Props {
    className?: string;
    children?: any;
    onClick?: () => void;

    size?: ButtonSize;
    color?: ButtonColor;
    width?: ButtonWidth;
    rightOrnament?: any;
    leftOrnament?: any;

    disabled?: boolean;
    loading?: boolean;
}

function Button({ children, className, size = ButtonSize.MEDIUM, color = ButtonColor.SECONDARY, width = ButtonWidth.AUTO, rightOrnament, leftOrnament, ...rest }: Props, ref: any) {
    return (
        <Wrapper ref={ref} {...rest} className={`${!children && 'isRound'} ${rightOrnament && 'hasRightOrnament'} ${leftOrnament && 'hasLeftOrnament'} ${className ? className : ''}`} size={size} _color={color} width={width}>
            {leftOrnament && leftOrnament}
            {children && <div className='button-text'>{children}</div>}
            {rightOrnament && rightOrnament}
        </Wrapper>
    )
}

export default forwardRef(Button);

const Wrapper = styled.button<{ size: ButtonSize, _color: ButtonColor, width: ButtonWidth }>`
    display: flex;
    align-items: center;
    justify-content: center;

    outline: none;
    box-shadow: none;
    border: none;
    cursor: pointer;

    font-weight: 700;
    color: #ffffff;

    &.hasRightOrnament .button-text{padding-right: 10px;}
    &.hasLeftOrnament .button-text{padding-left: 10px;}

    transition: background-color var(--ui-animation-duration), color var(--ui-animation-duration);

    ${({ size }) => sizes[size]}
    ${({ _color }) => colors[_color]}
    ${({ width }) => widths[width]}
`



const colors = {
    [ButtonColor.PRIMARY]: css`
        background-color: ${props => props.theme.primaryColor};

        &:hover {
            background-color: ${props => colorShade(props.theme.primaryColor, -0.15)};
        }
    `,
    [ButtonColor.SECONDARY]: css`
        background-color: ${props => colorShade(props.theme.backgroundColor, -0)};
        color: #dddddd;

        &:hover {
            background-color: ${props => colorShade(props.theme.backgroundColor, -0.2)};
            color: #ffffff;
        }
    `,

    [ButtonColor.TRANSPARENT]: css`
        background-color: transparent;
        color: #dddddd;

        &:hover {
            background-color: #ffffff22;
            color: #ffffff;
        }
    `,
}

const sizes = {
    [ButtonSize.SMALL]: css`
        height: 40px;
        border-radius: 20px;
        padding: 0 20px;

        font-size: 14px;

        &.isRound { padding: 0; width: 40px; }
    `,
    [ButtonSize.MEDIUM]: css`
        height: 50px;
        border-radius: 26px;
        padding: 0 30px;

        font-size: 16px;

        &.isRound { padding: 0; width: 50px; }
    `,
    [ButtonSize.LARGE]: css`
        height: 60px;
        border-radius: 30px;
        padding: 0 40px;

        font-size: 18px;

        &.isRound { padding: 0; width: 60px; }
    `,
}

const widths = {
    [ButtonWidth.AUTO]: css``,
    [ButtonWidth.CONTENT]: css`width: fit-content; &.isRound { width: fit-content}`,
    [ButtonWidth.FULL]: css`width: 100%; &.isRound { width: 100%}`,
}
